import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Car, Calendar, Gauge, Fuel, ShieldCheck, ArrowUpRight, Heart } from 'lucide-react'

import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { AuthModal } from '@/components/common'
import type { CarListing } from '@/types/marketplace.types'
import { appRoutes } from '@/constants'

interface CarCardProps {
  car: CarListing
  layout?: 'grid' | 'list'
}

export function CarCard({ car, layout = 'grid' }: CarCardProps) {
  const [authModalOpen, setAuthModalOpen] = useState(false)
  const isList = layout === 'list'

  const primaryImage = Array.isArray(car.images) && car.images.length > 0 
    ? typeof car.images[0] === 'string' ? car.images[0] : (car.images[0] as any)?.url
    : null

  const displayPrice = car.askingPrice 
    ? `₹${Number(car.askingPrice).toLocaleString('en-IN')}`
    : (car as any).priceText || 'Contact Seller'

  const displayYear = car.manufactureYear || (car.specs as any)?.year || 'N/A'
  const displayMileage = car.mileageKm ? `${car.mileageKm.toLocaleString('en-IN')} km` : (car.specs as any)?.mileage || 'N/A'
  const displayFuel = car.fuelType || (car.specs as any)?.fuel || 'Petrol'

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="h-full"
      >
        <Card
          className={`rounded-2xl border border-border bg-card overflow-hidden hover:border-primary/40 transition-all duration-300 group flex h-full shadow-xs hover:shadow-lg ${
            isList ? 'flex-col sm:flex-row' : 'flex-col'
          }`}
        >
          {/* Car Image Container */}
          <div
            className={`relative bg-slate-900/10 dark:bg-slate-900/60 flex items-center justify-center overflow-hidden shrink-0 ${
              isList ? 'w-full sm:w-72 h-48 sm:h-auto border-b sm:border-b-0 sm:border-r border-border' : 'h-52 w-full border-b border-border'
            }`}
          >
            {primaryImage ? (
              <img
                src={primaryImage}
                alt={car.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            ) : (
              <Car className="h-16 w-16 text-muted-foreground/30 group-hover:text-primary/40 transition-colors" />
            )}

            <div className="absolute top-3 left-3 flex flex-wrap gap-2">
              <Badge className="bg-primary/20 text-primary border border-primary/30 backdrop-blur-md font-bold">
                {(car as any).badgeText || car.condition || 'USED'}
              </Badge>
              {(car.user?.role === 'DEALER' || (car as any).verified) && (
                <Badge className="bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30 backdrop-blur-md font-bold">
                  <ShieldCheck className="h-3 w-3 mr-1" /> Verified
                </Badge>
              )}
            </div>

            {/* Quick Wishlist Trigger */}
            <button
              type="button"
              onClick={() => setAuthModalOpen(true)}
              className="absolute top-3 right-3 h-8 w-8 rounded-full bg-background/80 border border-border text-foreground flex items-center justify-center hover:bg-primary hover:border-primary hover:text-white transition-colors backdrop-blur-md shadow-xs"
              aria-label="Save to Wishlist"
            >
              <Heart className="h-4 w-4" />
            </button>
          </div>

          {/* Details Content */}
          <div className="p-5 sm:p-6 space-y-4 flex-1 flex flex-col justify-between">
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-widest text-primary font-bold">{car.brand?.name || (car as any).brand || 'CAR'}</span>
              <Link to={appRoutes.carDetails(car.slug)}>
                <h3 className="font-display text-lg sm:text-xl font-bold text-foreground group-hover:text-primary transition-colors line-clamp-1">
                  {car.title}
                </h3>
              </Link>
              <p className="text-xs text-muted-foreground line-clamp-1">{car.locationCity ? `${car.locationCity}, ${car.locationState}` : (car as any).subtitle || ''}</p>
            </div>

            {/* Quick Specs Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 py-3 border-y border-border text-xs text-muted-foreground font-medium">
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="truncate">{displayYear}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Gauge className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="truncate">{displayMileage}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Fuel className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="truncate">{displayFuel}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Car className="h-3.5 w-3.5 text-primary shrink-0" />
                <span className="truncate">{car.transmission || 'Manual'}</span>
              </div>
            </div>

            {/* Pricing & CTA */}
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wider block font-semibold">Asking Price</span>
                <span className="font-display text-xl font-extrabold text-foreground">{displayPrice}</span>
              </div>

              <Link to={appRoutes.carDetails(car.slug)} className="w-full sm:w-auto">
                <Button size="sm" variant="outline" className="w-full border-border hover:bg-primary hover:border-primary text-foreground hover:text-white font-semibold">
                  View Car <ArrowUpRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      </motion.div>

      <AuthModal
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
        initialMode="login"
        title="Save to Wishlist"
        description="Sign in or create an account to save favorite cars to your wishlist."
      />
    </>
  )
}
